/**
 * References Extension for TipTap (T-245, frontend half — decorations only)
 *
 * The Sistema de Referencias lives OUTSIDE the document content: references are
 * rows in a DB table ({id, document_id, target_sheet_id, target_anchor_text,
 * source_sheet_id, source_sheet_name, target_sheet_name, source_quote, reason,
 * created_at}) fetched via GET /documents/{documentId}/references. This
 * extension NEVER stores references as marks or nodes — it renders them as
 * ProseMirror DECORATIONS computed from `storage.refs`, so the document text
 * stays untouched.
 *
 * Two decoration families:
 *   a) CHIPS — at the end of each referenced block, a non-editable widget span
 *      with numbered buttons (1)..(n) per block (ordered by created_at). Clicking
 *      a chip dispatches the window event 'openReferenceCard' { referenceId } —
 *      the UI host (ReferencesUI) opens the floating card.
 *   b) FLASH — when storage.flashQuote is set, the block whose textContent
 *      contains that quote gets a node decoration (amber background via inline
 *      style, so no global CSS is needed). Non-permanent: the caller clears it
 *      with clearReferenceFlash().
 *
 * CONTRACT (fixed — T-246 wires it):
 *   Commands (TipTap):
 *     setReferences(refs)               — store refs + force re-render
 *     flashReferenceBlock(quoteText)    — set flashQuote + re-render
 *     clearReferenceFlash()             — flashQuote = null + re-render
 *   Window events (emitted here):
 *     'openReferenceCard'  detail { referenceId }   — from chip click
 *   Window events (consumed elsewhere):
 *     'referencesGoToSource' { reference }          — from the card (EditorPage
 *       switches sheet + calls flashReferenceBlock)
 *     'referenceCardClosed'                         — EditorPage calls
 *       clearReferenceFlash
 *     'referencesUpdated'                           — after a successful DELETE;
 *       EditorPage refetches and re-calls setReferences
 *
 * Block matching: the FIRST textblock (node.isTextblock) whose normalized
 * textContent (collapse \s+ → ' ', trim) contains the ref's normalized
 * target_anchor_text. Matching is at BLOCK level (textContent ignores mark
 * boundaries) — this is deliberately different from findReplace's per-text-node
 * matching, because a reference anchor may span bold/italic/colored runs.
 *
 * NOT registered here — T-246 wires ReferencesExtension into
 * ContinuousDocument's `extensions:` array.
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

export const REFERENCES_PLUGIN_KEY = new PluginKey('references');

/**
 * T-252: normalización TOLERANTE para casar texto citado con el documento.
 *
 * Evidencia del incidente: la IA guardó la cita «…Ay, veinte, numero redondo y
 * valiente.» mientras el documento dice «¡…!» y «número» (con acento). Un
 * `includes` sobre el texto crudo NO casa nunca y el resaltado fallaba en
 * silencio. Se comparan solo letras/números en minúsculas, sin acentos ni
 * puntuación.
 */
function normalizeText(text) {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // diacríticos
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ') // puntuación → espacio
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * T-252: posiciones de los bloques a resaltar para una cita.
 *
 * Una cita puede CRUZAR bloques (la IA une dos versos/párrafos en una sola
 * cadena). Primero se busca la cita completa dentro de un bloque; si ningún
 * bloque la contiene, se parte en oraciones y se devuelve TODO bloque que
 * contenga alguna oración de ≥ 4 palabras. Sin esto, una cita que abarca dos
 * párrafos no resalta nada.
 */
function findFlashBlocks(doc, quoteText) {
  const full = normalizeText(quoteText);
  if (!full) return [];

  const exact = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return;
    if (normalizeText(node.textContent).includes(full)) exact.push({ pos, node });
  });
  if (exact.length > 0) return exact;

  const pieces = String(quoteText)
    .split(/(?<=[.;:!?])\s+|\n+/)
    .map((s) => normalizeText(s))
    .filter((s) => s.split(' ').length >= 4);
  if (pieces.length === 0) return [];

  const partial = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return;
    const blockText = normalizeText(node.textContent);
    if (pieces.some((p) => blockText.includes(p))) partial.push({ pos, node });
  });
  return partial;
}

/**
 * Build the full DecorationSet from the current doc + storage.
 *
 * Recomputed on every setMeta and every docChanged (see Plugin.state.apply) —
 * same pattern as search-highlight / suggestion plugins.
 */
function buildDecorations(doc, storage) {
  const decos = [];
  const refs = Array.isArray(storage.refs) ? storage.refs : [];

  // --- CHIPS: group refs by the block they land on -------------------------
  // Map<blockPos, { node, refs: [...] }>
  const blockMap = new Map();

  doc.descendants((node, pos) => {
    if (!node.isTextblock) return;
    const normBlock = normalizeText(node.textContent);
    if (!normBlock) return;
    const matching = refs.filter((r) => {
      const anchor = normalizeText(r?.target_anchor_text);
      return anchor.length > 0 && normBlock.includes(anchor);
    });
    if (matching.length === 0) return;
    // Numbering is PER BLOCK, ordered by created_at ascending.
    matching.sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return ta - tb;
    });
    blockMap.set(pos, { node, refs: matching });
  });

  for (const [pos, { node, refs: blockRefs }] of blockMap) {
    // Widget sits at the END of the block content: pos + nodeSize - 1 is the
    // position of the block's closing tag (just past the last character).
    // side:1 keeps the widget anchored to the right when the cursor is at
    // that boundary.
    const widgetPos = pos + node.nodeSize - 1;
    decos.push(
      Decoration.widget(
        widgetPos,
        (view, getPos) => {
          const span = document.createElement('span');
          span.className = 'reference-chips';
          span.setAttribute('contenteditable', 'false');
          // Minimal inline styles — no global CSS dependency.
          span.style.fontSize = '0.7em';
          span.style.whiteSpace = 'nowrap';
          blockRefs.forEach((ref, idx) => {
            const btn = document.createElement('button');
            btn.className = 'reference-chip';
            btn.setAttribute('type', 'button');
            btn.setAttribute('data-reference-id', String(ref.id));
            btn.setAttribute('title', 'Ver referencia');
            btn.textContent = `(${idx + 1})`;
            btn.style.verticalAlign = 'super';
            btn.style.color = '#0284c7'; // sky-600
            btn.style.cursor = 'pointer';
            btn.style.marginLeft = '2px';
            btn.style.border = 'none';
            btn.style.background = 'none';
            btn.style.padding = '0';
            span.appendChild(btn);
          });
          return span;
        },
        { side: 1 }
      )
    );
  }

  // --- FLASH: highlight every block the quote covers (T-252) ---------------
  if (storage.flashQuote) {
    for (const { pos, node } of findFlashBlocks(doc, storage.flashQuote)) {
      decos.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: 'reference-flash',
          style:
            'background-color: rgba(250,204,21,0.35); box-shadow: inset 0 0 0 2px rgba(202,138,4,0.7); border-radius: 4px; transition: background-color 0.3s;',
        })
      );
    }
  }

  return DecorationSet.create(doc, decos);
}

export const ReferencesExtension = Extension.create({
  name: 'references',

  // T-245 (fix revisión): TipTap v2 inicializa storage vía addStorage(), no
  // acepta una clave de config `storage:` (quedaría {} y un warning en consola).
  addStorage() {
    return {
      refs: [],
      flashQuote: null,
    };
  },

  addCommands() {
    return {
      setReferences:
        (refs) =>
        ({ tr, dispatch }) => {
          this.storage.refs = Array.isArray(refs) ? refs : [];
          tr.setMeta(REFERENCES_PLUGIN_KEY, { type: 'refs' });
          if (dispatch) dispatch(tr);
          return true;
        },

      // T-248: devuelve FALSE si la cita todavía no está en el documento (hoja
      // recién cambiada: el editor remonta por key y su contenido llega async).
      // El llamador (EditorPage) reintenta hasta que devuelva true — antes se
      // aplicaba a ciegas tras un setTimeout fijo y el resaltado no aparecía.
      // Además lleva el bloque a la vista: un resaltado fuera del viewport es,
      // para el usuario, un resaltado que no ocurrió.
      flashReferenceBlock:
        (quoteText) =>
        ({ tr, state, dispatch }) => {
          // T-252: usa el MISMO matcher tolerante que las decoraciones (acentos,
          // puntuación y citas que cruzan bloques).
          const blocks = findFlashBlocks(state.doc, quoteText);
          if (blocks.length === 0) return false;
          const found = blocks[0].pos;
          this.storage.flashQuote = quoteText;
          tr.setMeta(REFERENCES_PLUGIN_KEY, { type: 'flash', quoteText });
          // scrollIntoView sigue a la SELECCIÓN: sin moverla, haría scroll a
          // donde estaba el cursor, no al bloque citado.
          try {
            tr.setSelection(TextSelection.near(tr.doc.resolve(found + 1)));
          } catch (e) {
            /* bloque no seleccionable: el resaltado igual se pinta */
          }
          if (dispatch) dispatch(tr.scrollIntoView());
          return true;
        },

      clearReferenceFlash:
        () =>
        ({ tr, dispatch }) => {
          this.storage.flashQuote = null;
          tr.setMeta(REFERENCES_PLUGIN_KEY, { type: 'flash', quoteText: null });
          if (dispatch) dispatch(tr);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const extension = this; // capture for closure access to storage

    const plugin = new Plugin({
      key: REFERENCES_PLUGIN_KEY,
      state: {
        init(_, state) {
          return buildDecorations(state.doc, extension.storage);
        },
        apply(tr, value, oldState, newState) {
          const meta = tr.getMeta(REFERENCES_PLUGIN_KEY);
          if (meta) {
            // refs or flash changed — full rebuild
            return buildDecorations(newState.doc, extension.storage);
          }
          if (tr.docChanged) {
            // Spec: recalculate on every docChanged (blocks may have moved /
            // content edited → matching + widget positions must follow).
            return buildDecorations(newState.doc, extension.storage);
          }
          return value;
        },
      },
      props: {
        decorations: (state) => REFERENCES_PLUGIN_KEY.getState(state) || DecorationSet.empty,

        // Chip click → open the floating card. Same pattern as
        // commentClickPlugin (handleClick + closest + CustomEvent).
        handleClick(view, pos, event) {
          const el = event.target && event.target.closest && event.target.closest('[data-reference-id]');
          if (!el) return false;
          const referenceId = el.getAttribute('data-reference-id');
          if (referenceId == null) return false;
          window.dispatchEvent(new CustomEvent('openReferenceCard', { detail: { referenceId } }));
          return true;
        },
      },
    });

    return [plugin];
  },
});

export default ReferencesExtension;
